/**
 * Language context.
 *
 * The toggle belongs in the header of every screen including the PIN pad. An operator
 * who cannot read English cannot navigate to a settings page to find the switch, so
 * burying it there would make the Spanish catalog unreachable by the people who need
 * it.
 *
 * The choice is remembered per device rather than per operator: a shared tablet at a
 * charging station is used by whoever walks up, and asking each of them to re-pick a
 * language every shift is friction with no payoff.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { en } from '../i18n/en.ts';
import { es } from '../i18n/es.ts';
import { translate, translatePlural, type Catalog, type TranslationVars } from './translate.ts';

export type Language = 'en' | 'es';

export interface I18n {
  readonly language: Language;
  readonly setLanguage: (language: Language) => void;
  /** Looks up `key`, substituting any `{placeholders}`. */
  readonly t: (key: TranslationKey, vars?: TranslationVars) => string;
  /** Picks the `_one` or `_other` form and makes `{count}` available. */
  readonly plural: (key: string, count: number) => string;
}

export type TranslationKey = keyof typeof en;

const CATALOGS: Record<Language, Catalog> = { en, es };

const STORAGE_KEY = 'tesi_lang';

const I18nContext = createContext<I18n | undefined>(undefined);

export function I18nProvider({ children }: { readonly children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);

  // Screen readers pronounce Spanish text with an English voice unless the document
  // says otherwise, which makes it close to unintelligible.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    persistLanguage(next);
  }, []);

  const value = useMemo<I18n>(() => {
    // English second, always: a key missing from Spanish falls back whole rather than
    // rendering blank. A sentence half in each language is worse than either.
    const catalogs = language === 'en' ? [en] : [CATALOGS[language], en];

    return {
      language,
      setLanguage,
      t: (key, vars) => translate(catalogs, key, vars),
      plural: (key, count) => translatePlural(catalogs, key, count),
    };
  }, [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const context = useContext(I18nContext);

  if (context === undefined) {
    throw new Error('useI18n must be used inside an I18nProvider');
  }

  return context;
}

function initialLanguage(): Language {
  const stored = readStoredLanguage();

  if (stored !== undefined) {
    return stored;
  }

  // Falls back to the phone's own setting, so a Spanish-speaking operator gets
  // Spanish on their very first scan without being told the toggle exists.
  return navigator.language.startsWith('es') ? 'es' : 'en';
}

function readStoredLanguage(): Language | undefined {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'en' || stored === 'es' ? stored : undefined;
  } catch {
    // Private browsing and locked-down device policies can both refuse storage.
    // A forgotten language preference must never stop the app loading.
    return undefined;
  }
}

function persistLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // See readStoredLanguage.
  }
}
